/**
 * Smart Tasks Service
 * FinTasks 2.0 - Phase 2
 * 
 * Intelligent task generation and auto-completion based on financial events.
 * This service creates smart tasks when financial events occur and
 * automatically completes tasks when related actions are taken.
 * 
 * SAFETY RULES:
 * - Does NOT modify budgetService, transactionService, or other existing services
 * - All functions are pure helpers that return task suggestions
 * - Actual task creation is handled by FinTasksContext
 * - All linking fields are optional and backward compatible
 * 
 * @version 2.0.0 - Phase 2
 * @date 2024-12-04
 */

import { TASK_PRIORITY, TASK_STATUS, LINKED_TYPE } from '@/services/finTasksService';

// ============================================================================
// SMART TASK TYPES
// ============================================================================

export const SMART_TASK_TYPE = {
  // Budget-related
  OVERSPEND_REVIEW: 'overspend_review',
  BUDGET_WARNING: 'budget_warning',
  ALLOCATE_BUDGET: 'allocate_budget',
  
  // Bill-related
  UPCOMING_BILL: 'upcoming_bill',
  OVERDUE_BILL: 'overdue_bill',
  
  // Income-related
  SALARY_RECEIVED: 'salary_received',
  INCOME_ALLOCATION: 'income_allocation',
  
  // Subscription-related
  SUBSCRIPTION_RENEWAL: 'subscription_renewal',
  SUBSCRIPTION_PRICE_CHANGE: 'subscription_price_change',
  
  // Savings-related
  GOAL_BEHIND: 'goal_behind',
  GOAL_CONTRIBUTION: 'goal_contribution',
  
  // Seasonal
  TAX_SEASON: 'tax_season',
  YEAR_END_REVIEW: 'year_end_review'
};

export const SMART_TASK_TRIGGER = {
  TRANSACTION_CREATED: 'transaction_created',
  BUDGET_EXCEEDED: 'budget_exceeded',
  BUDGET_WARNING: 'budget_warning',
  BILL_DUE: 'bill_due',
  INCOME_RECEIVED: 'income_received',
  GOAL_BEHIND: 'goal_behind',
  SUBSCRIPTION_CHANGE: 'subscription_change',
  MANUAL: 'manual'
};

// ============================================================================
// TASK LINKING SCHEMA (Phase 2 additions - all optional)
// ============================================================================

/**
 * Extended task fields for financial linking
 * These are OPTIONAL fields that extend the existing task schema
 */
export const TASK_LINK_FIELDS = {
  // Primary links
  relatedBudgetId: null,      // Link to specific budget
  relatedCategoryId: null,    // Link to category that triggered task
  relatedBillId: null,        // Link to recurring bill
  relatedTransactionId: null, // Link to transaction that triggered task
  relatedGoalId: null,        // Link to savings goal
  
  // Smart task metadata
  smartTaskType: null,        // SMART_TASK_TYPE value
  smartTaskTrigger: null,     // What triggered this task
  autoCompleteOn: null,       // Action that would auto-complete this task
  expiresAt: null,           // When this task becomes irrelevant
  
  // Prevent duplicates
  deduplicationKey: null      // Unique key to prevent duplicate tasks
};

// ============================================================================
// SMART TASK GENERATORS
// ============================================================================

/**
 * Generate a smart task for budget overspending
 * 
 * @param {Object} params
 * @param {string} params.budgetId - Budget ID
 * @param {string} params.categoryId - Category ID
 * @param {string} params.categoryName - Category display name
 * @param {number} params.overspendAmount - Amount overspent
 * @param {string} params.currency - Currency code
 * @returns {Object} Smart task template
 */
export function generateOverspendTask({
  budgetId,
  categoryId,
  categoryName,
  overspendAmount,
  currency = 'USD'
}) {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(overspendAmount);

  return {
    // Core task fields
    title: `Review overspend in ${categoryName}`,
    description: `You've exceeded your ${categoryName} budget by ${formattedAmount}. Review recent transactions and decide on adjustments.`,
    priority: TASK_PRIORITY.HIGH,
    isFinancialTask: true,
    
    // Due date: 1 day from now
    dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
    
    // Smart task linking
    relatedBudgetId: budgetId,
    relatedCategoryId: categoryId,
    linkedType: LINKED_TYPE.BUDGET,
    linkedRef: budgetId,
    linkedLabel: `${categoryName} Budget`,
    
    // Smart task metadata
    smartTaskType: SMART_TASK_TYPE.OVERSPEND_REVIEW,
    smartTaskTrigger: SMART_TASK_TRIGGER.BUDGET_EXCEEDED,
    autoCompleteOn: 'budget_adjusted',
    
    // Deduplication: one overspend task per category per week
    deduplicationKey: `overspend_${categoryId}_${getWeekKey()}`,
    
    // Tags
    tags: ['smart-task', 'overspend', 'budget']
  };
}

/**
 * Generate a smart task for budget warning (approaching limit)
 * 
 * @param {Object} params
 * @param {string} params.budgetId - Budget ID
 * @param {string} params.categoryId - Category ID
 * @param {string} params.categoryName - Category display name
 * @param {number} params.utilizationPercent - Current utilization (0-100)
 * @param {number} params.remainingAmount - Amount remaining
 * @param {string} params.currency - Currency code
 * @returns {Object} Smart task template
 */
export function generateBudgetWarningTask({
  budgetId,
  categoryId,
  categoryName,
  utilizationPercent,
  remainingAmount,
  currency = 'USD'
}) {
  const formattedRemaining = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(remainingAmount);

  return {
    title: `Watch ${categoryName} spending`,
    description: `You've used ${Math.round(utilizationPercent)}% of your ${categoryName} budget. Only ${formattedRemaining} remaining.`,
    priority: TASK_PRIORITY.MEDIUM,
    isFinancialTask: true,
    
    // Due date: 3 days from now
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    
    // Smart task linking
    relatedBudgetId: budgetId,
    relatedCategoryId: categoryId,
    linkedType: LINKED_TYPE.BUDGET,
    linkedRef: budgetId,
    linkedLabel: `${categoryName} Budget`,
    
    // Smart task metadata
    smartTaskType: SMART_TASK_TYPE.BUDGET_WARNING,
    smartTaskTrigger: SMART_TASK_TRIGGER.BUDGET_WARNING,
    autoCompleteOn: 'budget_period_ends',
    
    // Deduplication
    deduplicationKey: `warning_${categoryId}_${getWeekKey()}`,
    
    tags: ['smart-task', 'budget-warning', 'budget']
  };
}

/**
 * Generate a smart task for upcoming bill payment
 * 
 * @param {Object} params
 * @param {string} params.billId - Bill/subscription ID
 * @param {string} params.billName - Bill name
 * @param {number} params.amount - Bill amount
 * @param {Date} params.dueDate - Due date
 * @param {string} params.currency - Currency code
 * @returns {Object} Smart task template
 */
export function generateUpcomingBillTask({
  billId,
  billName,
  amount,
  dueDate,
  currency = 'USD'
}) {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);

  const dueDateObj = new Date(dueDate);
  const formattedDate = dueDateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });

  return {
    title: `Pay ${billName}`,
    description: `${billName} payment of ${formattedAmount} is due on ${formattedDate}.`,
    priority: TASK_PRIORITY.HIGH,
    isFinancialTask: true,
    estimatedAmount: amount,
    currency,
    
    // Due date: the bill due date
    dueDate: dueDateObj.toISOString(),
    
    // Reminder: 2 days before
    reminderAt: new Date(dueDateObj.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    
    // Smart task linking
    relatedBillId: billId,
    linkedType: LINKED_TYPE.BILL,
    linkedRef: billId,
    linkedLabel: billName,
    
    // Smart task metadata
    smartTaskType: SMART_TASK_TYPE.UPCOMING_BILL,
    smartTaskTrigger: SMART_TASK_TRIGGER.BILL_DUE,
    autoCompleteOn: 'bill_paid',
    
    // Deduplication: one task per bill per period
    deduplicationKey: `bill_${billId}_${getMonthKey()}`,
    
    tags: ['smart-task', 'bill', 'payment']
  };
}

/**
 * Generate a smart task for income allocation
 * 
 * @param {Object} params
 * @param {string} params.transactionId - Income transaction ID
 * @param {number} params.amount - Income amount
 * @param {string} params.source - Income source/description
 * @param {string} params.currency - Currency code
 * @returns {Object} Smart task template
 */
export function generateIncomeAllocationTask({
  transactionId,
  amount,
  source,
  currency = 'USD'
}) {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);

  return {
    title: 'Allocate income to budget',
    description: `You received ${formattedAmount} from ${source || 'income'}. Allocate it to your budget envelopes.`,
    priority: TASK_PRIORITY.MEDIUM,
    isFinancialTask: true,
    estimatedAmount: amount,
    currency,
    
    // Due date: 2 days from now
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    
    // Smart task linking
    relatedTransactionId: transactionId,
    linkedType: LINKED_TYPE.TRANSACTION,
    linkedRef: transactionId,
    linkedLabel: source || 'Income',
    
    // Smart task metadata
    smartTaskType: SMART_TASK_TYPE.INCOME_ALLOCATION,
    smartTaskTrigger: SMART_TASK_TRIGGER.INCOME_RECEIVED,
    autoCompleteOn: 'budget_allocated',
    
    // Deduplication
    deduplicationKey: `income_${transactionId}`,
    
    tags: ['smart-task', 'income', 'allocation']
  };
}

/**
 * Generate a smart task for savings goal falling behind
 * 
 * @param {Object} params
 * @param {string} params.goalId - Goal ID
 * @param {string} params.goalName - Goal name
 * @param {number} params.shortfall - Amount behind
 * @param {number} params.targetAmount - Total target
 * @param {number} params.currentAmount - Current saved
 * @param {string} params.currency - Currency code
 * @returns {Object} Smart task template
 */
export function generateGoalBehindTask({
  goalId,
  goalName,
  shortfall,
  targetAmount,
  currentAmount,
  currency = 'USD'
}) {
  const formattedShortfall = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(shortfall);

  const progressPercent = Math.round((currentAmount / targetAmount) * 100);

  return {
    title: `Catch up on ${goalName}`,
    description: `You're ${formattedShortfall} behind on your ${goalName} goal (${progressPercent}% complete). Consider making a contribution.`,
    priority: TASK_PRIORITY.MEDIUM,
    isFinancialTask: true,
    estimatedAmount: shortfall,
    currency,
    
    // Due date: 7 days from now
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    
    // Smart task linking
    relatedGoalId: goalId,
    goalId: goalId,
    linkedType: 'goal',
    linkedRef: goalId,
    linkedLabel: goalName,
    
    // Smart task metadata
    smartTaskType: SMART_TASK_TYPE.GOAL_BEHIND,
    smartTaskTrigger: SMART_TASK_TRIGGER.GOAL_BEHIND,
    autoCompleteOn: 'goal_contribution',
    
    // Deduplication
    deduplicationKey: `goal_behind_${goalId}_${getWeekKey()}`,
    
    tags: ['smart-task', 'savings', 'goal']
  };
}

/**
 * Generate a smart task for subscription renewal
 * 
 * @param {Object} params
 * @param {string} params.subscriptionId - Subscription ID
 * @param {string} params.subscriptionName - Subscription name
 * @param {number} params.amount - Renewal amount
 * @param {Date} params.renewalDate - Renewal date
 * @param {string} params.currency - Currency code
 * @returns {Object} Smart task template
 */
export function generateSubscriptionRenewalTask({
  subscriptionId,
  subscriptionName,
  amount,
  renewalDate,
  currency = 'USD'
}) {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);

  const renewalDateObj = new Date(renewalDate);
  const formattedDate = renewalDateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });

  return {
    title: `Review ${subscriptionName} renewal`,
    description: `${subscriptionName} renews on ${formattedDate} for ${formattedAmount}. Review if you still need this subscription.`,
    priority: TASK_PRIORITY.LOW,
    isFinancialTask: true,
    estimatedAmount: amount,
    currency,
    
    // Due date: 3 days before renewal
    dueDate: new Date(renewalDateObj.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    
    // Smart task linking
    relatedBillId: subscriptionId,
    linkedType: LINKED_TYPE.BILL,
    linkedRef: subscriptionId,
    linkedLabel: subscriptionName,
    
    // Smart task metadata
    smartTaskType: SMART_TASK_TYPE.SUBSCRIPTION_RENEWAL,
    smartTaskTrigger: SMART_TASK_TRIGGER.SUBSCRIPTION_CHANGE,
    autoCompleteOn: 'subscription_reviewed',
    expiresAt: renewalDateObj.toISOString(),
    
    // Deduplication
    deduplicationKey: `subscription_${subscriptionId}_${getMonthKey()}`,
    
    tags: ['smart-task', 'subscription', 'review']
  };
}

// ============================================================================
// AUTO-COMPLETE DETECTION
// ============================================================================

/**
 * Check if a task should be auto-completed based on a financial event
 * 
 * @param {Object} task - The task to check
 * @param {Object} event - The financial event that occurred
 * @param {string} event.type - Event type (e.g., 'transaction_created', 'budget_adjusted')
 * @param {Object} event.data - Event data
 * @returns {boolean} Whether the task should be auto-completed
 */
export function shouldAutoComplete(task, event) {
  if (!task.autoCompleteOn || !event?.type) {
    return false;
  }

  // Already completed
  if (task.status === TASK_STATUS.DONE || task.status === TASK_STATUS.ARCHIVED) {
    return false;
  }

  switch (task.autoCompleteOn) {
    case 'bill_paid':
      return (
        event.type === 'transaction_created' &&
        event.data?.categoryId === task.relatedCategoryId &&
        event.data?.type === 'expense'
      );

    case 'budget_adjusted':
      return (
        event.type === 'budget_updated' &&
        event.data?.budgetId === task.relatedBudgetId
      );

    case 'budget_allocated':
      return (
        event.type === 'envelope_updated' &&
        event.data?.allocated === true
      );

    case 'goal_contribution':
      return (
        event.type === 'goal_contribution' &&
        event.data?.goalId === task.relatedGoalId
      );

    case 'subscription_reviewed':
      return (
        event.type === 'task_manual_complete' &&
        task.smartTaskType === SMART_TASK_TYPE.SUBSCRIPTION_RENEWAL
      );

    case 'budget_period_ends':
      // This would be checked by a scheduler, not by events
      return false;

    default:
      return false;
  }
}

/**
 * Find all tasks that should be auto-completed for a given event
 * 
 * @param {Array} tasks - All tasks
 * @param {Object} event - The financial event
 * @returns {Array} Tasks that should be auto-completed
 */
export function findTasksToAutoComplete(tasks, event) {
  return tasks.filter(task => shouldAutoComplete(task, event));
}

// ============================================================================
// DEDUPLICATION
// ============================================================================

/**
 * Check if a smart task already exists (to prevent duplicates)
 * 
 * @param {Array} existingTasks - Current tasks
 * @param {Object} newTask - Proposed new task
 * @returns {boolean} Whether a duplicate exists
 */
export function hasDuplicateTask(existingTasks, newTask) {
  if (!newTask.deduplicationKey) {
    return false;
  }

  return existingTasks.some(task => 
    task.deduplicationKey === newTask.deduplicationKey &&
    task.status !== TASK_STATUS.DONE &&
    task.status !== TASK_STATUS.ARCHIVED
  );
}

/**
 * Filter out duplicate tasks from a list of suggestions
 * 
 * @param {Array} existingTasks - Current tasks
 * @param {Array} suggestions - Proposed new tasks
 * @returns {Array} Non-duplicate suggestions
 */
export function filterDuplicates(existingTasks, suggestions) {
  return suggestions.filter(task => !hasDuplicateTask(existingTasks, task));
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a week key for deduplication (YYYY-WW format)
 */
function getWeekKey() {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${weekNumber.toString().padStart(2, '0')}`;
}

/**
 * Get a month key for deduplication (YYYY-MM format)
 */
function getMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
}

/**
 * Get a day key for deduplication (YYYY-MM-DD format)
 */
function getDayKey() {
  return new Date().toISOString().split('T')[0];
}

// ============================================================================
// BATCH GENERATION
// ============================================================================

/**
 * Generate all smart tasks from current financial state
 * This is the main entry point for smart task generation
 * 
 * @param {Object} params
 * @param {Object} params.budgetSummary - Current budget state
 * @param {Array} params.upcomingBills - Bills due soon
 * @param {Array} params.goals - Savings goals
 * @param {Array} params.existingTasks - Current tasks for deduplication
 * @param {string} params.currency - User's currency
 * @returns {Array} Array of smart task suggestions
 */
export function generateAllSmartTasks({
  budgetSummary = {},
  upcomingBills = [],
  goals = [],
  existingTasks = [],
  currency = 'USD'
}) {
  const suggestions = [];

  // 1. Generate from budget status
  if (budgetSummary.lines) {
    for (const line of budgetSummary.lines) {
      // Overspend tasks
      if (line.status === 'OVER' && line.overspend > 0) {
        suggestions.push(generateOverspendTask({
          budgetId: line.budgetId || budgetSummary.budgetId,
          categoryId: line.categoryId,
          categoryName: line.categoryName || line.categoryId,
          overspendAmount: line.overspend,
          currency
        }));
      }
      
      // Warning tasks (80%+ utilization)
      if (line.status === 'WARNING' && line.utilisation >= 0.8) {
        suggestions.push(generateBudgetWarningTask({
          budgetId: line.budgetId || budgetSummary.budgetId,
          categoryId: line.categoryId,
          categoryName: line.categoryName || line.categoryId,
          utilizationPercent: line.utilisation * 100,
          remainingAmount: line.remaining || 0,
          currency
        }));
      }
    }
  }

  // 2. Generate from upcoming bills
  for (const bill of upcomingBills) {
    suggestions.push(generateUpcomingBillTask({
      billId: bill.id,
      billName: bill.name,
      amount: bill.amount,
      dueDate: bill.dueDate,
      currency
    }));
  }

  // 3. Generate from goals falling behind
  for (const goal of goals) {
    if (goal.period?.onTrack === false && goal.status === 'ACTIVE') {
      suggestions.push(generateGoalBehindTask({
        goalId: goal.id,
        goalName: goal.name,
        shortfall: goal.period?.shortfall || 0,
        targetAmount: goal.targetAmount,
        currentAmount: goal.currentAmount,
        currency
      }));
    }
  }

  // 4. Filter out duplicates
  return filterDuplicates(existingTasks, suggestions);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Types
  SMART_TASK_TYPE,
  SMART_TASK_TRIGGER,
  TASK_LINK_FIELDS,
  
  // Generators
  generateOverspendTask,
  generateBudgetWarningTask,
  generateUpcomingBillTask,
  generateIncomeAllocationTask,
  generateGoalBehindTask,
  generateSubscriptionRenewalTask,
  generateAllSmartTasks,
  
  // Auto-complete
  shouldAutoComplete,
  findTasksToAutoComplete,
  
  // Deduplication
  hasDuplicateTask,
  filterDuplicates
};
